import React, { FC, ReactNode } from "react";
import imageMap from "images/map-accueil.webp";
import { useTranslation } from "react-i18next";

export interface SectionHeroHome2Props {
  className?: string;
  heading: ReactNode;
  btnText: string;
  compteur?: string;
}

const SectionHeroHome2: FC<SectionHeroHome2Props> = ({
  className = "",
  heading,
  btnText,
  compteur = "0",
}) => {
  const { t, i18n } = useTranslation();

  const links = {
    en: {
      whereToGo: "/en/where-to-go",
      countryGuides: "/en/country-guides",
      reservationTool: "/en/reservation",
      example: "/en/travel-itinerary-demo",
      create: "/en/login",
    },
    fr: {
      whereToGo: "/ou-partir",
      countryGuides: "/guides-pays",
      reservationTool: "/reservation",
      example: "/itineraire-voyage-demo",
      create: "/login",
    },
  };

  const currentLanguageLinks = links[i18n.language as "en" | "fr"] || links.fr;

  const focusLieuInput = () => {
  const inputs = Array.from(
    document.querySelectorAll<HTMLInputElement>('[data-focus-lieu="true"]')
  );

  const visibleInput = inputs.find((input) => {
    const rect = input.getBoundingClientRect();
    const style = window.getComputedStyle(input);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0"
    );
  });

  if (!visibleInput) return;

  visibleInput.focus();

  const length = visibleInput.value.length;
  visibleInput.setSelectionRange(length, length);
};
  return (
    <section
      className={`
        relative
        overflow-hidden

        rounded-[36px]
        lg:rounded-[48px]

        bg-white
        dark:bg-neutral-950

        border
        border-neutral-100
        dark:border-neutral-800

        shadow-[0_30px_90px_rgba(15,23,42,0.08)]

        px-5
        py-8
        sm:px-8
        lg:px-10
        xl:px-12
        lg:py-12

        ${className}
      `}
      data-nc-id="SectionHeroHome2"
    >
     {/* FOND DOUX */}
<div
  className="
    pointer-events-none
    absolute
    inset-0

    bg-gradient-to-br
    from-violet-50/80
    via-white
    to-emerald-50/70

    dark:from-[#080816]
    dark:via-[#11102A]
    dark:to-[#061B18]
  "
/>

<div
  className="
    pointer-events-none
    absolute
    -top-32
    -left-32
    h-80
    w-80
    rounded-full
    bg-rose-200/30
    blur-3xl

    dark:bg-fuchsia-500/14
  "
/>

<div
  className="
    pointer-events-none
    absolute
    -bottom-32
    right-1/3
    h-96
    w-96
    rounded-full
    bg-emerald-200/30
    blur-3xl

    dark:bg-emerald-500/16
  "
/>

<div
  className="
    pointer-events-none
    absolute
    -right-32
    top-10
    h-96
    w-96
    rounded-full
    bg-violet-200/30
    blur-3xl

    dark:bg-violet-600/20
  "
/>

{/* LÉGER VOILE ÉTOILÉ EN DARK MODE */}
<div
  className="
    pointer-events-none
    absolute
    inset-0
    hidden
    dark:block
    opacity-[0.14]
    bg-[radial-gradient(circle_at_18%_24%,rgba(255,255,255,0.35)_0_1px,transparent_1px),radial-gradient(circle_at_78%_28%,rgba(255,255,255,0.24)_0_1px,transparent_1px),radial-gradient(circle_at_58%_78%,rgba(255,255,255,0.20)_0_1px,transparent_1px)]
    bg-[length:90px_90px,130px_130px,110px_110px]
  "
/>

<div className="relative grid items-center gap-10 lg:grid-cols-[1fr_0.95fr]">
        {/* GAUCHE : TEXTE */}
        <div className="text-left">
          <div
            className="
              mb-5
              inline-flex
              items-center
              gap-2

              rounded-full
              border
              border-violet-100
              dark:border-violet-900/50

              bg-white/80
              dark:bg-neutral-900/80

              px-4
              py-2

              text-sm
              font-semibold
              text-violet-700
              dark:text-violet-300

              shadow-sm
              backdrop-blur
            "
          >
            <span>{heading}</span>
          </div>

          <h1
            className="
              max-w-3xl

              text-2xl
              font-black
              tracking-tight
              text-neutral-950
              dark:text-white

              sm:text-3xl
              lg:text-4xl
              xl:text-5xl

              leading-[1.03]
            "
          >
            {t('titre1')}
            <span
              className="
                block
                bg-gradient-to-r
                from-violet-600
                via-purple-500
                to-rose-500
                bg-clip-text
                text-transparent
              "
            >
                  {t('titre2')}
            </span>
          </h1>

          <p
            className="
              mt-6
              max-w-2xl

              text-base
              leading-relaxed
              text-neutral-600
              dark:text-neutral-300

              sm:text-md
              lg:text-lg
            "
          >
               {t('description5')}<br/>{t('description6')}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
             onClick={(e) => {
    e.preventDefault();

    const isMobileOrTablet = window.innerWidth < 1024;

    if (isMobileOrTablet) {
      window.dispatchEvent(new CustomEvent("tripkygo:focus-pays-mobile"));
      return;
    }

    focusLieuInput();
  }}
              className="
                inline-flex
                items-center
                justify-center
                gap-2

                rounded-2xl

                bg-gradient-to-r
                from-violet-600
                to-purple-600

                px-6
                py-4

                text-sm
                font-bold
                text-white

                shadow-lg
                shadow-violet-600/25

                transition
                hover:-translate-y-0.5
                hover:shadow-xl
                hover:shadow-violet-600/30

                sm:text-base
              "
            >

              {t('creerMonItineraire')}
            </button>

            <a
              href="https://tripkygo.com/planificateur-voyage/26881"
              className="
                inline-flex
                items-center
                justify-center
                gap-2

                rounded-2xl

                border
                border-neutral-200
                dark:border-neutral-700

                bg-white/80
                dark:bg-neutral-900/80

                px-6
                py-4

                text-sm
                font-bold
                text-neutral-900
                dark:text-white

                shadow-sm
                backdrop-blur

                transition
                hover:-translate-y-0.5
                hover:shadow-md

                sm:text-base
              "
            >
              <i className="las la-play text-xl" />
              {t('voirExemple')}
            </a>
          </div>

          {compteur !== "0" && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              
              <a className="text-sm text-neutral-600 dark:text-neutral-400" href="#voyagesPublics">
                <span className="font-bold text-violet-600 dark:text-violet-300">
                  {compteur} {t('itineraires')}
                </span>{" "}
                {t('nbItineraires')}
              </a>
            </div>
          )}
        </div>

        {/* DROITE : PREVIEW APP */}
        {/* DROITE : PREVIEW APP */}
<div className="relative">
  <div className="absolute -inset-4 rounded-[40px] bg-gradient-to-br from-green-100/60 via-sky-100/40 to-blue-100/50 blur-2xl" />

  <div
    className="
      relative
      overflow-hidden

      rounded-[34px]

      bg-white
      dark:bg-neutral-900

      p-4

      shadow-2xl
      border
      border-neutral-100
      dark:border-neutral-800
    "
  >
    {/* MAP */}
    <div
      className="
        relative
        h-56
        overflow-hidden
        rounded-[28px]

        border
        border-neutral-100
        dark:border-neutral-800

        bg-neutral-100
      "
    >
      <img
        src={imageMap}
        alt="Carte du japon"
        className="
          absolute
          inset-0
          h-full
          w-full
          object-cover
        "
      />

      {/* Léger voile pour harmoniser avec le design */}
      <div className="absolute inset-0 bg-white/10" />


      {/* Badge pays */}
      <div
        className="
          absolute
          right-2
          bottom-2
          z-20

          rounded-xl

          bg-white/95
          dark:bg-neutral-950/95

          px-2
          py-0.5

          shadow-xl
          backdrop-blur
        "
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">🇯🇵</span>

          <div>
            <p className="text-sm font-black text-neutral-950 dark:text-white">
              {t('japon')}
            </p>
          </div>
        </div>
      </div>

     
    </div>

    {/* CARD ITINÉRAIRE */}
    <div
      className="
        relative
        mt-2

        rounded-[28px]

        bg-white
        dark:bg-neutral-950

        p-3

        shadow-xl
        border
        border-neutral-100
        dark:border-neutral-800
      "
    >
      {/* Header jour */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className="
              flex
              h-12
              w-12
              flex-shrink-0
              items-center
              justify-center

              rounded-xl

              bg-rose-50
              text-rose-500

              shadow-sm
            "
          >
            <i className="las la-calendar text-xl" />
          </div>

          <div>
            <p className="text-sm font-black text-neutral-950 dark:text-white">
              {t('jour')} 5
            </p>
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              {t('mardi')} 03/10/2028
            </p>
          </div>
        </div>

        <div
          className="
            inline-flex
            items-center
            gap-2

            rounded-full

            bg-green-50
            text-green-600

            px-3
            py-2

            text-sm
            font-black
          "
        >
          <span
            className="
              flex
              h-7
              w-7
              items-center
              justify-center

              rounded-full

              bg-white
              border
              border-green-100

              shadow-sm
            "
          >
            <i className="las la-train text-xl" />
          </span>
          Train
        </div>
      </div>

      {/* Titre trajet */}
      <h3 className="mt-2 text-md font-black text-neutral-950 dark:text-white">
        Hakone <span className="text-neutral-400">→</span> Kyoto
      </h3>

      {/* Timeline trajet */}
<div className="mt-5 grid grid-cols-[22px_56px_minmax(0,1fr)_auto] grid-rows-[auto_28px_auto] items-center gap-x-4 text-xs">
  {/* Point départ */}
  <div className="col-start-1 row-start-1 flex justify-center">
    <span
      className="
        relative
        z-10
        h-2
        w-2
        rounded-full
        border-[3px]
        border-green-500
        bg-white
        dark:bg-neutral-950
      "
    />
  </div>

  {/* Ligne pointillée */}
  <div className="col-start-1 row-start-2 flex h-full justify-center">
    <span
      className="
        h-full
        w-px
        border-l-2
        border-dashed
        border-neutral-300
        dark:border-neutral-700
      "
    />
  </div>

  {/* Point arrivée */}
  <div className="col-start-1 row-start-3 flex justify-center">
    <span
      className="
        relative
        z-10
        h-2
        w-2
        rounded-full
        border-[3px]
        border-green-500
        bg-white
        dark:bg-neutral-950
      "
    />
  </div>

  {/* Heure départ */}
  <div className="col-start-2 row-start-1 font-black text-neutral-950 dark:text-white">
    08:09
  </div>

  {/* Libellé départ */}
  <div className="col-start-3 row-start-1 min-w-0 text-neutral-600 dark:text-neutral-300">
    <span className="block truncate">
      {t("gareHakone")}
    </span>
  </div>

  {/* Durée */}
  <div className="col-start-4 row-start-1 text-neutral-400">
    4h09
  </div>

  {/* Heure arrivée */}
  <div className="col-start-2 row-start-3 font-black text-neutral-950 dark:text-white">
    12:18
  </div>

  {/* Libellé arrivée */}
  <div className="col-start-3 row-start-3 min-w-0 text-neutral-600 dark:text-neutral-300">
    <span className="block truncate">
      {t("gareKyoto")}
    </span>
  </div>
</div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div
          className="
            rounded-2xl
            bg-violet-50
            dark:bg-violet-950/30

            p-4
            text-center
          "
        >
          <i className="las la-wallet text-xl md:text-2xl text-violet-600" />
          <p className="mt-1 text-xs md:text-xl font-black text-violet-700 dark:text-violet-300">
            2 240 €
          </p>
          <p className="text-xs text-neutral-500">Budget</p>
        </div>

        <div
          className="
            rounded-2xl
            bg-green-50
            dark:bg-green-950/30

            p-4
            text-center
          "
        >
          <i className="las la-calendar text-xl md:text-2xl text-green-600" />
          <p className="mt-1 text-xs md:text-xl font-black text-green-700 dark:text-green-300">
            15
          </p>
          <p className="text-xs text-neutral-500">{t('joursMajuscule')}</p>
        </div>

        <div
          className="
            rounded-2xl
            bg-orange-50
            dark:bg-orange-950/30

            p-4
            text-center
          "
        >
          <i className="las la-map-marker-alt text-xl md:text-2xl text-orange-600" />
          <p className="mt-1 text-xs md:text-xl font-black text-orange-700 dark:text-orange-300">
            5
          </p>
          <p className="text-xs text-neutral-500">{t('lieuxMajuscule')}</p>
        </div>
      </div>
    </div>
  </div>
</div>
</div>

      {/* LIENS BAS */}
      <div
        className="
          relative
          mt-10

          grid
          gap-3

          border-t
          border-neutral-200/70
          dark:border-neutral-800

          pt-6

          md:grid-cols-3
        "
      >
        <a
          href={currentLanguageLinks.whereToGo}
          className="
            group
            flex
            items-center
            gap-4

            rounded-3xl
            bg-white/70
            dark:bg-neutral-900/70

            p-4

            transition
            hover:bg-white
            hover:shadow-md
            dark:hover:bg-neutral-900
          "
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
            <i className="las la-globe text-2xl" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-bold text-neutral-950 dark:text-white">
              {t("ouPartir")}
            </span>
            <span className="block text-sm text-neutral-500">
              {t('trouvezInspiration')}
            </span>
          </span>
          <i className="las la-angle-right text-neutral-400 group-hover:text-violet-600" />
        </a>

        <a
          href={currentLanguageLinks.countryGuides}
          className="
            group
            flex
            items-center
            gap-4

            rounded-3xl
            bg-white/70
            dark:bg-neutral-900/70

            p-4

            transition
            hover:bg-white
            hover:shadow-md
            dark:hover:bg-neutral-900
          "
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <i className="las la-map text-2xl" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-bold text-neutral-950 dark:text-white">
              {t("guidesPays")}
            </span>
            <span className="block text-sm text-neutral-500">
              {t('decouvrezIncontournables')}
            </span>
          </span>
          <i className="las la-angle-right text-neutral-400 group-hover:text-emerald-600" />
        </a>

        <a
          href={currentLanguageLinks.reservationTool}
          className="
            group
            flex
            items-center
            gap-4

            rounded-3xl
            bg-white/70
            dark:bg-neutral-900/70

            p-4

            transition
            hover:bg-white
            hover:shadow-md
            dark:hover:bg-neutral-900
          "
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
            <i className="las la-ticket-alt text-2xl" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-bold text-neutral-950 dark:text-white">
              {t("outilReservation")}
            </span>
            <span className="block text-sm text-neutral-500">
              {t('activitesTransportsHebergements')}
            </span>
          </span>
          <i className="las la-angle-right text-neutral-400 group-hover:text-orange-600" />
        </a>
      </div>
    </section>
  );
};

export default SectionHeroHome2;